import { ArrowUpRight, ChevronDown, ChevronRight, Download, Lock, Pencil } from 'lucide-react';
import { messageForCode } from '@obraplan/core/application/errors/error-codes';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { consolidateProject, splitByPurchase } from '@obraplan/core/domain/takeoff/consolidation';
import { priceLabor } from '@obraplan/core/domain/takeoff/labor-rate';
import { currentPricePeriod, priceMaterials } from '@obraplan/core/domain/takeoff/material-price';
import {
  approvedServiceLabel,
  groupBudget,
  mergeApprovedBudgets,
  serviceLockKey,
  summarizeBudget,
  type ApprovedBudget,
  type BudgetDiscipline,
  type BudgetLine,
  type BudgetService,
  type ServiceApproval,
} from '@obraplan/core/domain/takeoff/project-budget';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { isPurchasable } from '@obraplan/core/domain/takeoff/takeoff-line';
import { EmptyState, LoadingState } from '@/components/feedback/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { downloadXlsx, toXlsx } from '@/features/reports/xlsx';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { mesPorExtenso } from '@/features/workforce/custo-da-folha';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';
import { formatBusinessDate } from '@/lib/format';
import { cn } from '@/lib/cn';
import { aptosDaObra, type MedidosDe } from './aptos-da-obra';
import {
  nomeDoArquivoDoOrcamento,
  orcamentoEmPlanilha,
  type DadosDoOrcamento,
} from './exportar-orcamento';
import { metros, precoUnitario } from './formato';
import { medidosDaTipologia, totaisDaTipologia } from './medido-da-tipologia';
import { fundacaoDaTipologia } from './resumo-dos-itens';
import {
  orcamentoDaObra,
  ROTULO_CURTO_DA_ORIGEM_NO_ORCAMENTO,
  ROTULO_DA_ORIGEM,
  TOM_DA_ORIGEM_NO_ORCAMENTO,
} from './orcamento-da-obra';
import { reais } from './precos-da-obra';
import { Segmentado } from './Segmentado';
import { textoDaAprovacao } from './servicos-aprovados';
import type { TypologyDraft, WorkspaceDraft } from './typology-draft';
import { useCustoDaMaoDeObra } from './use-custo-da-mao-de-obra';
import { usePrecosDaObra } from './use-precos-da-obra';

/**
 * O orçamento da obra: o **custo direto** — material mais mão de obra, sem BDI — em três
 * níveis, disciplina → serviço → item, cada um com o total (Fase 29, etapa 4 — D15, D17).
 *
 * A Consolidação responde "quanto de cada material a obra compra"; esta responde "quanto cada
 * disciplina e cada serviço custam". É a mesma conta com a outra pergunta: cada material entra
 * uma vez por serviço que o usa, com a parte do serviço na quantidade e o preço do material.
 *
 * Duas peças, de propósito:
 *
 * - `QuadroDoOrcamento` só **mostra** linhas (`BudgetLine`). Não sabe de onde vieram, e por isso
 *   serve igual ao orçamento vivo e ao serviço aprovado, que o servidor guarda como foto;
 * - `OrcamentoDaObra` **monta** as linhas vivas — a obra inteira, consolidada e com os preços e
 *   o R$/h que valem agora —, põe no lugar de cada serviço aprovado a foto dele (Fase 33,
 *   RN-437) e entrega ao quadro.
 *
 * Aqui nada se edita: o preço e o R$/h são decididos em Itens e consolidação, que é onde se vê o
 * material inteiro e de onde ele vem. Este quadro só os lê.
 */

// ── O quadro: linhas prontas, na tela ───────────────────────────────────────────

type Mostrar = 'TUDO' | 'FORA_DO_TOTAL';

export interface QuadroDoOrcamentoProps {
  /** O nome da obra — vai no arquivo e na aba "Resumo" da planilha. */
  readonly obra?: string;
  /** As linhas na ordem da obra, com o total de cada uma. */
  readonly linhas: readonly BudgetLine[];
  /** O período da foto do compras que valia (RN-397), `AAAA-MM-DD`. */
  readonly periodoDosPrecos?: { readonly from: string; readonly to: string };
  /** O mês das horas do R$/h, `AAAA-MM` (RN-399). */
  readonly mesDaMaoDeObra?: string;
  /**
   * Os serviços aprovados, pela chave da trava (`serviceLockKey`): quando e por quem. As linhas
   * deles já são as da foto — o quadro só diz quais são (Fase 33, RN-437).
   */
  readonly aprovados?: ReadonlyMap<string, ServiceApproval>;
  /** Uma frase sobre o que o quadro cobre — "a obra inteira, sem o filtro do topo". */
  readonly escopo?: string;
  /** Para onde ir decidir os preços; ausente, o quadro não oferece o caminho. */
  readonly onAbrirItens?: () => void;
  /** Botões a mais na barra, ao lado de "Exportar Excel" — o "Aprovar serviços…". */
  readonly acoes?: ReactNode;
}

export function QuadroDoOrcamento({
  obra,
  linhas,
  periodoDosPrecos,
  mesDaMaoDeObra,
  aprovados,
  escopo,
  onAbrirItens,
  acoes,
}: QuadroDoOrcamentoProps) {
  const idDoQuadro = useId();
  const [mostrar, setMostrar] = useState<Mostrar>('TUDO');
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(new Set());

  const disciplinas = useMemo(() => groupBudget(linhas), [linhas]);
  const resumo = useMemo(() => summarizeBudget(linhas), [linhas]);
  const servicos = disciplinas.flatMap((disciplina) => disciplina.services);
  const quantosAprovados =
    aprovados === undefined
      ? 0
      : servicos.filter((servico) => aprovados.has(serviceLockKey(servico.key))).length;

  /**
   * Só o que está fora do total, para quem vai completar o orçamento: as disciplinas sem
   * pendência somem, e os totais ficam os de sempre — o que se filtra é a lista, e não a conta.
   */
  const visiveis = useMemo(
    () =>
      mostrar === 'TUDO'
        ? disciplinas
        : disciplinas
            .map((disciplina) => ({
              ...disciplina,
              services: disciplina.services
                .map((servico) => ({
                  ...servico,
                  lines: servico.lines.filter((linha) => linha.total === undefined),
                }))
                .filter((servico) => servico.lines.length > 0),
            }))
            .filter((disciplina) => disciplina.services.length > 0),
    [disciplinas, mostrar],
  );

  const aberta = (nome: string): boolean => mostrar === 'FORA_DO_TOTAL' || abertas.has(nome);
  const todasAbertas = visiveis.length > 0 && visiveis.every((d) => aberta(d.name));
  const alternar = (nome: string): void =>
    setAbertas((atuais) => {
      const proximas = new Set(atuais);
      if (!proximas.delete(nome)) proximas.add(nome);
      return proximas;
    });

  const exportar = (): void => {
    const agora = new Date();
    const dados: DadosDoOrcamento = {
      obra: obra ?? '',
      linhas,
      geradoEm: agora,
      ...(periodoDosPrecos === undefined ? {} : { periodoDosPrecos }),
      ...(mesDaMaoDeObra === undefined ? {} : { mesDaMaoDeObra }),
      ...(aprovados === undefined ? {} : { aprovados }),
    };
    downloadXlsx(nomeDoArquivoDoOrcamento(obra ?? '', agora), toXlsx(orcamentoEmPlanilha(dados)));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>Orçamento da obra</CardTitle>
          {quantosAprovados > 0 && (
            <Badge tone="ok" data-servicos-aprovados>
              {quantosAprovados === servicos.length
                ? 'todos os serviços aprovados'
                : `${String(quantosAprovados)} de ${contar(servicos.length, 'serviço aprovado', 'serviços aprovados')}`}
            </Badge>
          )}
        </div>
        <CardDescription>
          O custo direto, disciplina por disciplina e serviço por serviço: material e mão de obra,
          sem BDI.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div
          className="grid gap-px overflow-hidden rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-border-subtle) sm:grid-cols-3"
          role="group"
          aria-label="Totais do orçamento"
        >
          <Total titulo="Material" valor={resumo.material} dado="data-total-material" />
          <Total titulo="Mão de obra" valor={resumo.labor} dado="data-total-mao-de-obra" />
          <Total
            titulo="Custo direto"
            valor={resumo.total}
            dado="data-custo-direto"
            destaque
            nota="sem BDI"
          />
        </div>

        {resumo.linesOutOfTotal > 0 ? (
          <p className="text-sm text-(--color-state-warn)" data-fora-do-total>
            {`${String(resumo.linesOutOfTotal)} ${resumo.linesOutOfTotal === 1 ? 'linha fora' : 'linhas fora'} do total`}
            <span className="text-(--color-content-muted)">
              {` — ${textoDasFaltas(resumo.materialsWithoutPrice, resumo.laborWithoutRate)}. O custo direto fica menor do que a obra vai custar até elas terem preço.`}
            </span>
          </p>
        ) : (
          linhas.length > 0 && (
            <p className="text-sm text-(--color-content-muted)">
              Todas as linhas têm preço e entram no total.
            </p>
          )
        )}

        <p className="text-xs text-(--color-content-muted)" data-base-do-orcamento>
          {[
            periodoDosPrecos === undefined
              ? undefined
              : `Preços do compras: ${formatBusinessDate(periodoDosPrecos.from)} a ${formatBusinessDate(periodoDosPrecos.to)}`,
            mesDaMaoDeObra === undefined
              ? undefined
              : `Mão de obra: ${mesPorExtenso(mesDaMaoDeObra)}`,
            'Custo direto, sem BDI',
          ]
            .filter((parte): parte is string => parte !== undefined)
            .join(' · ')}
        </p>

        {escopo !== undefined && (
          <p className="text-xs text-(--color-content-muted)">
            <span className="font-medium text-(--color-content)">{escopo}</span>
          </p>
        )}

        {linhas.length === 0 ? (
          <EmptyState
            title="Nenhum item com quantidade"
            description="O orçamento sai do quantitativo: nenhum serviço da obra tem item com quantidade lançada. Lance as quantidades em Itens e consolidação."
            {...(onAbrirItens === undefined
              ? {}
              : { action: { label: 'Abrir Itens e consolidação', onClick: onAbrirItens } })}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Segmentado<Mostrar>
                label="O que a lista mostra"
                atual={mostrar}
                opcoes={[
                  { valor: 'TUDO', rotulo: 'Tudo' },
                  {
                    valor: 'FORA_DO_TOTAL',
                    rotulo: 'Fora do total',
                    detalhe: String(resumo.linesOutOfTotal),
                  },
                ]}
                onEscolher={setMostrar}
              />
              {mostrar === 'TUDO' && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    setAbertas(todasAbertas ? new Set() : new Set(visiveis.map((d) => d.name)))
                  }
                >
                  {todasAbertas ? 'Recolher tudo' : 'Abrir tudo'}
                </Button>
              )}
              <div className="ml-auto flex flex-wrap items-center gap-2">
                {onAbrirItens !== undefined && (
                  <Button variant="ghost" onClick={onAbrirItens}>
                    <Pencil className="size-4" aria-hidden="true" /> Editar preços
                  </Button>
                )}
                {acoes}
                <Button variant="secondary" onClick={exportar}>
                  <Download className="size-4" aria-hidden="true" /> Exportar Excel
                </Button>
              </div>
            </div>

            {visiveis.length === 0 ? (
              <p className="text-sm text-(--color-content-muted)">Nenhuma linha fora do total.</p>
            ) : (
              <div className="flex flex-col divide-y divide-(--color-border-subtle) rounded-md border border-(--color-border-subtle)">
                {visiveis.map((disciplina, indice) => (
                  <DisciplinaDoOrcamento
                    key={disciplina.name}
                    id={`${idDoQuadro}-${String(indice)}`}
                    disciplina={disciplina}
                    {...(aprovados === undefined ? {} : { aprovados })}
                    aberta={aberta(disciplina.name)}
                    onAlternar={() => alternar(disciplina.name)}
                    recolhivel={mostrar === 'TUDO'}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** "1 material sem preço e 2 funções sem custo" — quem falta, dito pelo nome da falta. */
function textoDasFaltas(materiais: number, funcoes: number): string {
  const partes = [
    materiais > 0
      ? `${String(materiais)} ${materiais === 1 ? 'material sem preço' : 'materiais sem preço'}`
      : undefined,
    funcoes > 0
      ? `${String(funcoes)} ${funcoes === 1 ? 'função sem custo' : 'funções sem custo'}`
      : undefined,
  ].filter((parte): parte is string => parte !== undefined);
  return partes.join(' e ');
}

/** Um número da faixa do topo; o custo direto é o que se lê primeiro, e por isso é maior. */
function Total({
  titulo,
  valor,
  dado,
  destaque = false,
  nota,
}: {
  titulo: string;
  valor: number;
  dado: string;
  destaque?: boolean;
  nota?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 bg-(--color-surface-raised) p-4">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
        {titulo}
      </span>
      <span
        {...{ [dado]: true }}
        className={cn(
          'tabular break-words leading-none tracking-tight',
          destaque ? 'text-2xl font-bold' : 'text-xl font-semibold',
        )}
      >
        {reais(valor)}
      </span>
      {nota !== undefined && <span className="text-xs text-(--color-content-muted)">{nota}</span>}
    </div>
  );
}

/**
 * Uma disciplina: o cabeçalho com o que ela soma, e — aberta — um serviço atrás do outro, cada
 * um com o subtotal e as linhas.
 *
 * O cabeçalho é um botão que recolhe e abre (`aria-expanded`): um orçamento de obra inteira tem
 * dezenas de disciplinas, e a primeira leitura é o resumo delas, não as milhares de linhas.
 */
function DisciplinaDoOrcamento({
  id,
  disciplina,
  aprovados,
  aberta,
  onAlternar,
  recolhivel,
}: {
  id: string;
  disciplina: BudgetDiscipline;
  aprovados?: ReadonlyMap<string, ServiceApproval>;
  aberta: boolean;
  onAlternar: () => void;
  /** Na vista das pendências o bloco fica aberto: é para onde a pessoa veio olhar. */
  recolhivel: boolean;
}) {
  const quantasLinhas = disciplina.services.reduce(
    (soma, servico) => soma + servico.lines.length,
    0,
  );
  const aprovacaoDe = (servico: BudgetService): ServiceApproval | undefined =>
    aprovados?.get(serviceLockKey(servico.key));
  const quantosAprovados = disciplina.services.filter(
    (servico) => aprovacaoDe(servico) !== undefined,
  ).length;
  return (
    <section aria-label={disciplina.name}>
      <button
        type="button"
        aria-expanded={aberta}
        aria-controls={id}
        disabled={!recolhivel}
        onClick={onAlternar}
        className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-left hover:bg-(--color-surface-muted) disabled:cursor-default disabled:hover:bg-transparent"
      >
        <span className="flex min-w-0 flex-1 basis-56 items-center gap-2">
          {aberta ? (
            <ChevronDown
              className="size-4 shrink-0 text-(--color-content-muted)"
              aria-hidden="true"
            />
          ) : (
            <ChevronRight
              className="size-4 shrink-0 text-(--color-content-muted)"
              aria-hidden="true"
            />
          )}
          <span className="min-w-0">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.08em]">
              {disciplina.name}
            </span>
            <span className="block text-xs text-(--color-content-muted)">
              {`${contar(disciplina.services.length, 'serviço', 'serviços')} · ${contar(quantasLinhas, 'linha', 'linhas')}`}
              {quantosAprovados > 0 && (
                <span className="text-(--color-state-ok)">
                  {` · ${quantosAprovados === disciplina.services.length ? 'aprovada' : `${String(quantosAprovados)} ${quantosAprovados === 1 ? 'aprovado' : 'aprovados'}`}`}
                </span>
              )}
              {disciplina.linesOutOfTotal > 0 && (
                <span className="text-(--color-state-warn)">
                  {` · ${String(disciplina.linesOutOfTotal)} fora do total`}
                </span>
              )}
            </span>
          </span>
        </span>
        <span className="flex flex-wrap items-baseline justify-end gap-x-5 gap-y-0.5 text-sm">
          <Parte titulo="Material" valor={disciplina.material} />
          <Parte titulo="Mão de obra" valor={disciplina.labor} />
          <Parte titulo="Total" valor={disciplina.total} forte />
        </span>
      </button>

      {aberta && (
        <div id={id} className="border-t border-(--color-border-subtle)">
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="w-16">Un.</TableHead>
                  <TableHead className="w-32 text-right">Quantidade</TableHead>
                  <TableHead className="w-56 text-right">Preço unit.</TableHead>
                  <TableHead className="w-36 text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              {disciplina.services.map((servico) => {
                const aprovacao = aprovacaoDe(servico);
                return (
                  <tbody
                    key={servico.key}
                    className="border-b border-(--color-border-subtle) last:border-0"
                  >
                    <TableRow
                      className="bg-(--color-surface-muted) hover:bg-(--color-surface-muted)"
                      {...(aprovacao === undefined ? {} : { 'data-servico-aprovado': servico.key })}
                    >
                      <TableCell
                        colSpan={4}
                        className="py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)"
                      >
                        {servico.name}
                        {aprovacao !== undefined && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 font-medium normal-case tracking-normal text-(--color-state-ok)"
                            title="Serviço aprovado: a quantidade e o preço são os da aprovação, e não mudam mais."
                          >
                            <Lock className="size-3" aria-hidden="true" />
                            {textoDaAprovacao(aprovacao)}
                          </span>
                        )}
                        {servico.linesOutOfTotal > 0 && (
                          <span className="font-medium normal-case tracking-normal text-(--color-state-warn)">
                            {` · ${String(servico.linesOutOfTotal)} fora do total`}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="tabular py-1.5 text-right text-sm font-semibold">
                        {reais(servico.total)}
                      </TableCell>
                    </TableRow>
                    {servico.lines.map((linha, indice) => (
                      <LinhaDoOrcamento key={`${linha.itemKey}-${String(indice)}`} linha={linha} />
                    ))}
                  </tbody>
                );
              })}
            </Table>
          </TableWrapper>
        </div>
      )}
    </section>
  );
}

/** `1 serviço` · `3 serviços`. */
function contar(quantos: number, singular: string, plural: string): string {
  return `${String(quantos)} ${quantos === 1 ? singular : plural}`;
}

/** Um dos três números do cabeçalho de uma disciplina, com o nome em cima. */
function Parte({
  titulo,
  valor,
  forte = false,
}: {
  titulo: string;
  valor: number;
  forte?: boolean;
}) {
  return (
    <span className="flex flex-col items-end">
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
        {titulo}
      </span>
      <span className={cn('tabular', forte && 'font-semibold')}>{reais(valor)}</span>
    </span>
  );
}

/**
 * Uma linha: o item, a parte do serviço na quantidade, o preço com a origem e o total.
 *
 * A linha sem preço diz por que — o aviso é a frase do domínio — e fica sem total: ela está
 * **fora da soma**, e a coluna do total vazia é o que o diz sem depender da cor (§4.4).
 */
function LinhaDoOrcamento({ linha }: { linha: BudgetLine }) {
  const semPreco = linha.unitPrice === undefined;
  const aviso = linha.warning ?? (semPreco ? 'sem preço' : undefined);
  const legenda = [linha.code, isPurchasable(linha.kind) ? undefined : 'Mão de obra']
    .filter((parte): parte is string => parte !== undefined)
    .join(' · ');
  return (
    <TableRow className="border-0">
      <TableCell>
        <span className="font-medium">{linha.description}</span>
        {legenda !== '' && (
          <span className="block font-mono text-xs text-(--color-content-muted)">{legenda}</span>
        )}
      </TableCell>
      <TableCell className="text-xs">{linha.unit ?? '—'}</TableCell>
      <TableCell className="tabular text-right">{metros(linha.quantity, 3)}</TableCell>
      <TableCell className="text-right">
        <span className="flex flex-col items-end gap-0.5">
          <span className="flex flex-wrap items-center justify-end gap-1.5">
            <span className={cn('tabular', !semPreco && 'font-medium')}>
              {linha.unitPrice === undefined ? '—' : precoUnitario(linha.unitPrice)}
            </span>
            {linha.priceOrigin !== undefined && (
              <Badge
                tone={TOM_DA_ORIGEM_NO_ORCAMENTO[linha.priceOrigin]}
                title={ROTULO_DA_ORIGEM[linha.priceOrigin]}
                className="px-1.5 py-0 text-[11px]"
              >
                {ROTULO_CURTO_DA_ORIGEM_NO_ORCAMENTO[linha.priceOrigin]}
              </Badge>
            )}
          </span>
          {aviso !== undefined && (
            <span className="text-[11px] text-(--color-state-warn)">{aviso}</span>
          )}
        </span>
      </TableCell>
      <TableCell className="tabular text-right font-semibold">
        {linha.total === undefined ? '—' : reais(linha.total)}
      </TableCell>
    </TableRow>
  );
}

// ── O orçamento vivo: monta as linhas da obra inteira ───────────────────────────

/**
 * O orçamento vivo da obra aberta.
 *
 * Soma a **obra inteira** — todas as tipologias, cada uma vezes os pavimentos que a repetem, e
 * o apto que diverge contado à parte —, como o "Obra inteira" da Consolidação, e por isso o
 * custo daqui e o pedido de compra de lá falam do mesmo quantitativo. O filtro de disciplina e
 * serviço do topo da página não entra: ele recorta o que as listas mostram, e um orçamento
 * recortado pelo filtro seria um total que a obra não tem. O recorte é o de cada tipologia sem
 * o filtro (`recorteDe`).
 *
 * Lê os preços e o R$/h aqui, e só os lê: esta aba monta sozinha, sem as tabelas de Itens e
 * consolidação ao lado, e quem decide um preço é aquela. Sem os dois, o orçamento não se monta —
 * um custo direto só com o material seria um número incompleto sem cara de incompleto.
 */
export function OrcamentoDaObra({
  workspace,
  catalogo,
  recorteDe,
  onAbrirItens,
}: {
  workspace: WorkspaceDraft;
  /** O catálogo da obra, com as disciplinas na ordem de execução (`naOrdemDaObra`). */
  catalogo: ServiceCatalog;
  /** O recorte de cada tipologia, **sem** o filtro do topo. */
  recorteDe: (typology: TypologyDraft) => ServiceCatalog;
  onAbrirItens?: () => void;
}) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const precos = usePrecosDaObra();
  const custo = useCustoDaMaoDeObra();
  const projeto = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProject.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  /*
   * Os serviços aprovados (Fase 33, RN-437): as fotos, guardadas no servidor. O orçamento da obra
   * põe a foto no lugar de cada serviço aprovado; "Atual" mostra tudo como está hoje, para
   * comparar. Quem edita o quantitativo aprova os serviços abertos (D13).
   */
  const aprovacoesLidas = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProjectApprovedBudget.execute({ projectId: activeProjectId }, session),
    [session?.userId, activeProjectId],
  );
  const aprovacoes: readonly ApprovedBudget[] | undefined =
    aprovacoesLidas !== undefined && aprovacoesLidas.ok
      ? aprovacoesLidas.value.approvals
      : undefined;
  const [vista, setVista] = useState<'ORCAMENTO' | 'ATUAL'>('ORCAMENTO');
  const [escolhendo, setEscolhendo] = useState(false);

  /*
   * A versão leva serviços para a obra aprovada (Fase 38, RN-487): o nome dela vai no botão, e os
   * serviços que ela já aprovou não se oferecem — eles não reabrem (RN-490).
   */
  const aprovadaId = projeto !== undefined && projeto.ok ? projeto.value.versionOf : undefined;
  const aprovada = useLive(
    () =>
      session === undefined || aprovadaId === undefined
        ? Promise.resolve(undefined)
        : container.getProject.execute(aprovadaId, session),
    [session?.userId, aprovadaId],
  );
  const aprovacoesDaAprovada = useLive(
    () =>
      session === undefined || aprovadaId === undefined
        ? Promise.resolve(undefined)
        : container.getProjectApprovedBudget.execute({ projectId: aprovadaId }, session),
    [session?.userId, aprovadaId],
  );
  const [levando, setLevando] = useState(false);
  const [levar, levada] = useCommand(
    (chaves: readonly string[]) =>
      container.transferServicesToApproved.execute(
        { projectId: activeProjectId, serviceKeys: [...chaves] },
        session!,
      ),
    {
      successMessage: (resultado) =>
        resultado.services.length === 1
          ? `Serviço levado para ${resultado.approved.name}. Ele chegou aberto: aprove lá quando quiser.`
          : `${String(resultado.services.length)} serviços levados para ${resultado.approved.name}. Eles chegaram abertos: aprove lá quando quiser.`,
    },
  );
  const [aprovar, aprovacao] = useCommand(
    (pedido: {
      linhas: readonly BudgetLine[];
      periodo?: { from: string; to: string };
      mes?: string;
    }) =>
      container.approveProjectBudget.execute(
        {
          projectId: activeProjectId,
          // O total de cada linha o servidor refaz; mandar o da tela só seria ignorado.
          lines: pedido.linhas.map(({ total: _total, ...linha }) => linha),
          ...(pedido.periodo === undefined ? {} : { pricePeriod: pedido.periodo }),
          ...(pedido.mes === undefined ? {} : { laborMonth: pedido.mes }),
        },
        session!,
      ),
    {
      successMessage: (resultado) =>
        resultado.budget.services.length === 1
          ? 'Serviço aprovado — a quantidade e o preço dele não mudam mais.'
          : `${String(resultado.budget.services.length)} serviços aprovados — a quantidade e o preço deles não mudam mais.`,
      onSuccess: () => setVista('ORCAMENTO'),
    },
  );

  /**
   * O medido de cada serviço em cada tipologia, uma vez por tipologia: a soma da obra o pede a
   * cada parcela, e a conta do desenho não é barata.
   */
  const medidosDe: MedidosDe = useMemo(() => {
    const guardados = new Map<string, Readonly<Record<string, number>>>();
    const base = workspace.catalog ?? catalogo;
    return (typology) => {
      const guardado = guardados.get(typology.unitTypeId);
      if (guardado !== undefined) return guardado;
      // A taxa de aço por peça da fundação muda o medido do aço (Fase 41, RN-545).
      const medidos = medidosDaTipologia(
        base,
        totaisDaTipologia(workspace, typology),
        typology,
        fundacaoDaTipologia(workspace, typology),
      );
      guardados.set(typology.unitTypeId, medidos);
      return medidos;
    };
  }, [workspace, catalogo]);

  const itens = useMemo(
    () => consolidateProject(catalogo, aptosDaObra(workspace, recorteDe, medidosDe)),
    [workspace, catalogo, recorteDe, medidosDe],
  );

  const mapaDePrecos = precos.precos;
  const taxas = custo.custo?.taxas;
  const orcamento = useMemo(() => {
    if (mapaDePrecos === undefined || taxas === undefined) return undefined;
    const { materials, labor } = splitByPurchase(itens);
    return orcamentoDaObra(
      priceMaterials(materials, mapaDePrecos, precos.fatoresDoProduto).rows,
      priceLabor(labor, taxas).rows,
      catalogo,
    );
  }, [itens, mapaDePrecos, taxas, precos.fatoresDoProduto, catalogo]);

  const periodo = useMemo(
    () => (mapaDePrecos === undefined ? undefined : currentPricePeriod([...mapaDePrecos.values()])),
    [mapaDePrecos],
  );

  const falhas = [
    precos.erro === undefined ? undefined : `Os preços não foram lidos: ${precos.erro}`,
    custo.erro === undefined ? undefined : `O custo da mão de obra não foi lido: ${custo.erro}`,
    // Sem as fotos, o serviço aprovado sairia com os números de hoje, como se estivesse aberto.
    aprovacoesLidas === undefined || aprovacoesLidas.ok
      ? undefined
      : `Os serviços aprovados não foram lidos: ${messageForCode(aprovacoesLidas.error.code, aprovacoesLidas.error.message)}`,
  ].filter((falha): falha is string => falha !== undefined);

  if (falhas.length > 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Orçamento da obra</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {falhas.map((falha) => (
            <p key={falha} role="alert" className="text-sm text-(--color-state-warn)">
              {falha}
            </p>
          ))}
          <p className="text-xs text-(--color-content-muted)">
            Sem eles, o custo direto sairia incompleto — o orçamento só abre com preço, R$/h e os
            serviços aprovados.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (orcamento === undefined || aprovacoes === undefined) {
    return (
      <Card>
        <CardContent className="pt-5">
          <LoadingState label="Lendo os preços, o custo da mão de obra e os serviços aprovados…" />
        </CardContent>
      </Card>
    );
  }

  const nomeDaObra = projeto !== undefined && projeto.ok ? projeto.value.name : undefined;
  const ehVersao = projeto !== undefined && projeto.ok && projeto.value.versionOf !== undefined;
  const escopo =
    'Sempre a obra inteira: todas as tipologias, cada uma vezes os pavimentos que a repetem. O filtro de disciplina e serviço do topo não vale aqui.';

  const daObra = mergeApprovedBudgets(orcamento.linhas, aprovacoes ?? []);
  const temAprovado = daObra.approved.size > 0;
  const noOrcamento = !temAprovado || vista === 'ORCAMENTO';

  // Os serviços de hoje que ainda não foram aprovados — os que o "Aprovar serviços…" oferece.
  const abertos = groupBudget(orcamento.linhas).flatMap((disciplina) =>
    disciplina.services
      .filter((servico) => !daObra.approved.has(serviceLockKey(servico.key)))
      .map((servico) => ({ disciplina: disciplina.name, servico })),
  );
  // Só quem edita o quantitativo aprova (D13), só na obra aprovada (RN-436), e só depois de saber
  // o que já está aprovado — senão a lista ofereceria o que o servidor vai recusar.
  const podeAprovar =
    precos.podeEditar && !ehVersao && aprovacoes !== undefined && abertos.length > 0;

  // A versão leva para a aprovada (Fase 38): quem edita o quantitativo, depois de saber o que a
  // aprovada já aprovou.
  const nomeDaAprovada = aprovada !== undefined && aprovada.ok ? aprovada.value.name : undefined;
  const travadosNaAprovada =
    aprovacoesDaAprovada !== undefined && aprovacoesDaAprovada.ok
      ? new Set(
          aprovacoesDaAprovada.value.approvals.flatMap((foto) =>
            foto.services.map((servico) => servico.key),
          ),
        )
      : undefined;
  const podeLevar =
    ehVersao &&
    precos.podeEditar &&
    nomeDaAprovada !== undefined &&
    travadosNaAprovada !== undefined &&
    orcamento.linhas.length > 0;
  const botaoDeLevar = podeLevar ? (
    <Button variant="primary" disabled={levada.isPending} onClick={() => setLevando(true)}>
      <ArrowUpRight className="size-4" aria-hidden="true" /> Levar para {nomeDaAprovada}…
    </Button>
  ) : undefined;

  return (
    <div className="flex flex-col gap-3">
      {temAprovado && (
        <div className="flex flex-wrap items-center gap-3">
          <Segmentado<'ORCAMENTO' | 'ATUAL'>
            label="Qual orçamento"
            atual={vista}
            opcoes={[
              { valor: 'ORCAMENTO', rotulo: 'Orçamento' },
              { valor: 'ATUAL', rotulo: 'Atual' },
            ]}
            onEscolher={setVista}
          />
          <p className="flex items-center gap-1.5 text-xs text-(--color-content-muted)">
            <Lock className="size-3.5" aria-hidden="true" />
            {noOrcamento
              ? 'Os serviços aprovados são a foto da aprovação: a quantidade e o preço daquele dia, que não mudam. Os outros são os de hoje.'
              : 'O quantitativo e os preços de hoje, também nos serviços aprovados, para comparar com a foto.'}
          </p>
        </div>
      )}
      {noOrcamento ? (
        <QuadroDoOrcamento
          {...(nomeDaObra === undefined ? {} : { obra: nomeDaObra })}
          linhas={daObra.lines}
          {...(periodo === undefined ? {} : { periodoDosPrecos: periodo })}
          {...(custo.custo === undefined ? {} : { mesDaMaoDeObra: custo.custo.mes })}
          {...(temAprovado ? { aprovados: daObra.approved } : {})}
          escopo={escopo}
          {...(onAbrirItens === undefined ? {} : { onAbrirItens })}
          {...(podeAprovar
            ? {
                acoes: (
                  <Button
                    variant="primary"
                    disabled={aprovacao.isPending}
                    onClick={() => setEscolhendo(true)}
                  >
                    <Lock className="size-4" aria-hidden="true" /> Aprovar serviços…
                  </Button>
                ),
              }
            : botaoDeLevar === undefined
              ? {}
              : { acoes: botaoDeLevar })}
        />
      ) : (
        <QuadroDoOrcamento
          {...(nomeDaObra === undefined ? {} : { obra: nomeDaObra })}
          linhas={orcamento.linhas}
          {...(periodo === undefined ? {} : { periodoDosPrecos: periodo })}
          {...(custo.custo === undefined ? {} : { mesDaMaoDeObra: custo.custo.mes })}
          escopo={escopo}
          {...(onAbrirItens === undefined ? {} : { onAbrirItens })}
        />
      )}
      {ehVersao && precos.podeEditar && (
        <p className="text-xs text-(--color-content-muted)" data-versao-nao-aprova>
          {nomeDaAprovada === undefined
            ? 'Esta obra é uma versão de orçamento: os serviços são aprovados na obra aprovada, e a versão herda os que já estão aprovados.'
            : `Esta obra é uma versão de orçamento: os serviços são aprovados em ${nomeDaAprovada}. Use "Levar para ${nomeDaAprovada}" para mandar para lá o quantitativo dos serviços que você montou aqui, e aprove-os lá.`}
        </p>
      )}
      {levando && nomeDaAprovada !== undefined && travadosNaAprovada !== undefined && (
        <LevarServicos
          aprovada={nomeDaAprovada}
          disciplinas={groupBudget(orcamento.linhas)}
          travados={travadosNaAprovada}
          pendente={levada.isPending}
          onFechar={() => setLevando(false)}
          onLevar={(chaves) => {
            setLevando(false);
            void levar(chaves);
          }}
        />
      )}
      {escolhendo && (
        <AprovarServicos
          abertos={abertos}
          pendente={aprovacao.isPending}
          onFechar={() => setEscolhendo(false)}
          onAprovar={(chaves) => {
            setEscolhendo(false);
            void aprovar({
              linhas: orcamento.linhas.filter((linha) => chaves.has(linha.serviceKey)),
              ...(periodo === undefined ? {} : { periodo }),
              ...(custo.custo === undefined ? {} : { mes: custo.custo.mes }),
            });
          }}
        />
      )}
    </div>
  );
}

/**
 * Escolher os serviços que a versão leva para a obra aprovada (Fase 38, RN-487 a RN-490).
 *
 * A lista é a do orçamento da versão, por disciplina, e a disciplina marca de uma vez todos os
 * serviços dela. O serviço que a aprovada já aprovou aparece, mas não marca: ele não reabre.
 */
function LevarServicos({
  aprovada,
  disciplinas,
  travados,
  pendente,
  onFechar,
  onLevar,
}: {
  aprovada: string;
  disciplinas: readonly BudgetDiscipline[];
  travados: ReadonlySet<string>;
  pendente: boolean;
  onFechar: () => void;
  onLevar: (chaves: readonly string[]) => void;
}) {
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const livres = (disciplina: BudgetDiscipline) =>
    disciplina.services
      .filter((servico) => !travados.has(serviceLockKey(servico.key)))
      .map((servico) => servico.key);
  const alternar = (chaves: readonly string[], marcar: boolean): void =>
    setMarcados((atuais) => {
      const proximos = new Set(atuais);
      for (const chave of chaves) {
        if (marcar) proximos.add(chave);
        else proximos.delete(chave);
      }
      return proximos;
    });

  return (
    <ConfirmDialog
      open
      onOpenChange={(aberto) => {
        if (!aberto) onFechar();
      }}
      title={`Levar para ${aprovada}`}
      description={`O quantitativo dos serviços marcados vai desta versão para ${aprovada} e substitui o que ela tem deles: os itens, a quantidade por peça e por tipologia e o lugar das peças na planta. Eles chegam abertos — aprove-os lá. O cronograma de lá fica como está; o "Atualizar" dele relê a quantidade nova. Esta versão não muda.`}
      confirmLabel={
        marcados.size <= 1 ? 'Levar serviço' : `Levar ${String(marcados.size)} serviços`
      }
      confirmDisabled={pendente || marcados.size === 0}
      onConfirm={() => onLevar([...marcados])}
    >
      <ul
        className="mt-4 flex max-h-96 flex-col overflow-y-auto rounded-md border border-(--color-border-subtle)"
        aria-label="Serviços da versão"
      >
        {disciplinas.map((disciplina) => {
          const chaves = livres(disciplina);
          const todos = chaves.length > 0 && chaves.every((chave) => marcados.has(chave));
          return (
            <li
              key={disciplina.name}
              className="border-b border-(--color-border-subtle) last:border-b-0"
            >
              <label
                className={cn(
                  'flex items-center gap-3 px-3 py-2 text-sm font-semibold',
                  chaves.length === 0 ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
                )}
              >
                <input
                  type="checkbox"
                  checked={todos}
                  disabled={chaves.length === 0}
                  aria-label={`Marcar a disciplina ${disciplina.name} inteira`}
                  onChange={() => alternar(chaves, !todos)}
                />
                <span className="min-w-0 flex-1">{disciplina.name}</span>
                <span className="text-xs font-normal text-(--color-content-muted)">
                  {contar(disciplina.services.length, 'serviço', 'serviços')}
                </span>
              </label>
              <ul className="divide-y divide-(--color-border-subtle)">
                {disciplina.services.map((servico) => {
                  const aprovado = travados.has(serviceLockKey(servico.key));
                  return (
                    <li key={servico.key}>
                      <label
                        className={cn(
                          'flex items-center gap-3 py-2 pr-3 pl-9 text-sm',
                          aprovado ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={marcados.has(servico.key)}
                          disabled={aprovado}
                          onChange={() => alternar([servico.key], !marcados.has(servico.key))}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block">{servico.name}</span>
                          {aprovado && (
                            <span className="block text-xs text-(--color-state-warn)">
                              {`Já aprovado em ${aprovada} — não reabre`}
                            </span>
                          )}
                        </span>
                        <span className="tabular text-right">{reais(servico.total)}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </ConfirmDialog>
  );
}

/**
 * Escolher os serviços a aprovar (Fase 33, RN-435, RN-436).
 *
 * A lista é a dos serviços abertos, na ordem da obra, com o total de cada um. O serviço com linha
 * sem preço aparece, mas não marca: ele não reabre, e a linha ficaria fora do total para sempre
 * (D9). A confirmação diz que o aprovado não volta atrás.
 */
function AprovarServicos({
  abertos,
  pendente,
  onFechar,
  onAprovar,
}: {
  abertos: readonly { readonly disciplina: string; readonly servico: BudgetService }[];
  pendente: boolean;
  onFechar: () => void;
  onAprovar: (chaves: ReadonlySet<string>) => void;
}) {
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const aprovaveis = abertos.filter(({ servico }) => servico.linesOutOfTotal === 0);
  const alternar = (chave: string): void =>
    setMarcados((atuais) => {
      const proximos = new Set(atuais);
      if (!proximos.delete(chave)) proximos.add(chave);
      return proximos;
    });
  const total = abertos
    .filter(({ servico }) => marcados.has(servico.key))
    .reduce((soma, { servico }) => soma + servico.total, 0);

  return (
    <ConfirmDialog
      open
      onOpenChange={(aberto) => {
        if (!aberto) onFechar();
      }}
      title="Aprovar serviços"
      description="Guarda a foto dos serviços marcados — a quantidade, o preço e o total de cada linha — com a data e o seu nome. Serviço aprovado não reabre: a quantidade e o preço dele não mudam mais. Os outros continuam abertos."
      confirmLabel={
        marcados.size <= 1 ? 'Aprovar serviço' : `Aprovar ${String(marcados.size)} serviços`
      }
      confirmDisabled={pendente || marcados.size === 0}
      onConfirm={() => onAprovar(marcados)}
    >
      <div className="mt-4 flex flex-col gap-3">
        {aprovaveis.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <Button
              variant="ghost"
              onClick={() =>
                setMarcados(
                  marcados.size === aprovaveis.length
                    ? new Set()
                    : new Set(aprovaveis.map(({ servico }) => servico.key)),
                )
              }
            >
              {marcados.size === aprovaveis.length ? 'Desmarcar todos' : 'Marcar todos com preço'}
            </Button>
          </div>
        )}
        <ul
          className="flex max-h-80 flex-col divide-y divide-(--color-border-subtle) overflow-y-auto rounded-md border border-(--color-border-subtle)"
          aria-label="Serviços abertos"
        >
          {abertos.map(({ disciplina, servico }) => {
            const semPreco = servico.linesOutOfTotal > 0;
            return (
              <li key={servico.key}>
                <label
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 text-sm',
                    semPreco ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={marcados.has(servico.key)}
                    disabled={semPreco}
                    onChange={() => alternar(servico.key)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">
                      {approvedServiceLabel({
                        disciplineName: disciplina,
                        serviceName: servico.name,
                      })}
                    </span>
                    {semPreco && (
                      <span className="block text-xs text-(--color-state-warn)">
                        {`${contar(servico.linesOutOfTotal, 'linha sem preço', 'linhas sem preço')} — informe o provisório para aprovar`}
                      </span>
                    )}
                  </span>
                  <span className="tabular text-right">{reais(servico.total)}</span>
                </label>
              </li>
            );
          })}
        </ul>
        {marcados.size > 0 && (
          <p className="text-sm" data-total-a-aprovar>
            {`${contar(marcados.size, 'serviço marcado', 'serviços marcados')}, custo direto de ${reais(total)}.`}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
